import { useEffect } from 'react';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { App } from '@capacitor/app';
import * as stores from '../stores';

/**
 * Hook to manage keyboard visibility state on native platforms.
 *
 * This hook:
 * 1. Listens to keyboard show/hide events and updates the store
 * 2. Resets keyboard state when app returns to foreground (fixes navbar disappearing
 *    when app is resumed after being suspended with keyboard open)
 *
 * Should be called once at the app root level (AppWrapper).
 */
export const useKeyboardState = () => {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Track all listener promises to handle cleanup race condition
    // If component unmounts before promises resolve, we still clean up properly
    const listenerPromises: Promise<PluginListenerHandle>[] = [];

    // Local mirror of keyboard visibility. Set and cleared exclusively by keyboard event
    // handlers (keyboardWillShow/Hide, keyboardDidShow/Hide). The foreground reset guard
    // reads this flag to make the correct decision regardless of whether keyboard events
    // arrive before or after appStateChange — event order is non-guaranteed on all
    // platforms. appStateChange never writes this flag.
    let isKeyboardCurrentlyVisible = false;

    // Used to cancel the appStateChange keyboard reset if a keyboard event fires first.
    // Prevents a race where appStateChange resets state after keyboardWillShow/Did has
    // already set it to visible. Combined with the isKeyboardCurrentlyVisible guard,
    // keyboard events take precedence over the reset regardless of delivery order.
    let pendingForegroundReset: ReturnType<typeof setTimeout> | null = null;

    const cancelPendingReset = () => {
      if (pendingForegroundReset !== null) {
        clearTimeout(pendingForegroundReset);
        pendingForegroundReset = null;
      }
    };

    // Use predictive keyboard events (keyboardWillShow/Hide) for both iOS and Android
    // to enable smooth WhatsApp-style keyboard animations. Android does support these
    // events and they provide earlier notification than keyboardDid* events.
    //
    // Note: This was previously using keyboardDid* for Android, but ChatPage was adding
    // duplicate keyboardWill* listeners for smoother animations. Unified to keyboardWill*
    // here to eliminate redundant listeners and improve consistency.
    listenerPromises.push(
      Keyboard.addListener('keyboardWillShow', (info) => {
        isKeyboardCurrentlyVisible = true;
        cancelPendingReset();
        stores.General.update((s) => {
          s.isKeyboardVisible = true;
          s.keyboardHeight = info.keyboardHeight;
        });
      }),
    );

    listenerPromises.push(
      Keyboard.addListener('keyboardWillHide', () => {
        isKeyboardCurrentlyVisible = false;
        stores.General.update((s) => {
          s.isKeyboardVisible = false;
          s.keyboardHeight = 0;
        });
      }),
    );

    // Fallback: keyboardDid* events fire after the animation completes and act as a
    // safety net for cases where keyboardWill* events are missed — e.g., app returning
    // to foreground with keyboard already open, third-party keyboards, or edge cases
    // on certain iOS versions. If Will already fired, these are no-ops (state matches).
    listenerPromises.push(
      Keyboard.addListener('keyboardDidShow', (info) => {
        isKeyboardCurrentlyVisible = true;
        cancelPendingReset();
        stores.General.update((s) => {
          s.isKeyboardVisible = true;
          s.keyboardHeight = info.keyboardHeight;
        });
      }),
    );

    listenerPromises.push(
      Keyboard.addListener('keyboardDidHide', () => {
        isKeyboardCurrentlyVisible = false;
        stores.General.update((s) => {
          s.isKeyboardVisible = false;
          s.keyboardHeight = 0;
        });
      }),
    );

    // Reset keyboard state when app returns to foreground.
    // This prevents the navbar from staying hidden if the keyboard was open when the
    // app was suspended (and keyboardWillHide didn't fire during backgrounding).
    //
    // Uses a short delay (300ms) so any keyboard events that fire on resume can cancel
    // the reset first — if the keyboard is still showing, keyboardWillShow/Did will
    // arrive and call cancelPendingReset() before the timeout fires.
    //
    // The timeout also checks isKeyboardCurrentlyVisible before resetting, making the
    // logic robust to non-guaranteed event ordering. Two cases are handled:
    //   - Keyboard events fire BEFORE appStateChange (active): the flag is already true,
    //     the scheduled timeout reads it and skips the reset. ✓
    //   - Keyboard events fire AFTER appStateChange (active): the show handler calls
    //     cancelPendingReset() before the timeout fires. ✓
    //
    // isKeyboardCurrentlyVisible is cleared when going to background so that the
    // delayed reset fires by default on the next resume. Keyboard events on resume
    // (keyboardDidShow) set the flag back to true before the timeout fires, cancelling
    // the reset when the keyboard is actually still showing.
    listenerPromises.push(
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          cancelPendingReset();
          pendingForegroundReset = setTimeout(() => {
            pendingForegroundReset = null;
            if (!isKeyboardCurrentlyVisible) {
              stores.General.update((s) => {
                s.isKeyboardVisible = false;
                s.keyboardHeight = 0;
              });
            }
          }, 300);
        } else {
          // Cancel any pending reset and clear the visibility flag when going to background.
          // We reset the flag here so that on the next foreground transition the delayed
          // reset will fire by default (guard passes), unless a keyboard event fires first
          // to set it back to true (handling the "keyboard still open on resume" case).
          cancelPendingReset();
          isKeyboardCurrentlyVisible = false;
        }
      }),
    );

    // Cleanup listeners on unmount - wait for all promises to resolve
    return () => {
      cancelPendingReset();
      Promise.all(listenerPromises).then((listeners) => {
        listeners.forEach((listener) => listener.remove());
      });
    };
  }, []);
};

export default useKeyboardState;
